import I18n from 'i18n-js';
import React, { useContext } from 'react';

import { RCCheckbox, RCIcon, RCRadioGroup } from '@reverbdotcom/cadence/components';
import { parseAmount } from '@reverbdotcom/commons/src/money';
import { FormCards } from '@reverbdotcom/commons/src/components/FormCards';
import { ProtectionShieldIcon } from '@reverbdotcom/cadence/icons/react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';

import { SellFormContext } from '../../SellFormContext';
import { Field, ActionType } from '../../sellFormReducerTypes';
import { packageEntryComplete } from '../../../../discovery/shipping_labels/shipping_label_package';

const SAFE_SHIPPING_ARTICLE_ID = '360002670474';

const COUPLED_PROTECTION_TIER_MIN_USD_CENTS = 10000;
const COUPLED_PROTECTION_TIER_MAX_USD_CENTS = 150000;
const RSS_MAX_USD_CENTS = 1000000;

enum FormVariant {
  RSS_OPTIONAL_ONLY = 'rssOptionalOnly',
  SIGNATURE_AND_RSS_BOTH_OPTIONAL = 'signatureAndRssBothOptional',
  COUPLED_PROTECTION = 'coupledProtection',
  ALWAYS_COVERED = 'alwaysCovered',
}

function getFormVariant(currentPriceCents: number, isUps: boolean): FormVariant {
  if (currentPriceCents < COUPLED_PROTECTION_TIER_MIN_USD_CENTS) {
    // $0 - $99.99
    // -------------
    // UPS: seller can opt in/out of RSS (signature always included)
    // USPS: seller can opt in/out of RSS or signature, independent of each other
    return isUps ? FormVariant.RSS_OPTIONAL_ONLY : FormVariant.SIGNATURE_AND_RSS_BOTH_OPTIONAL;
  } else if (currentPriceCents <= COUPLED_PROTECTION_TIER_MAX_USD_CENTS) {
    // $100 - $1,500
    // -------------
    // UPS: seller can opt in/out of RSS (signature always included)
    // USPS: seller can choose from 3 radio options: [RSS+signature], signature only, or no protection
    return isUps ? FormVariant.RSS_OPTIONAL_ONLY : FormVariant.COUPLED_PROTECTION;
  } else if (currentPriceCents > COUPLED_PROTECTION_TIER_MAX_USD_CENTS) {
    // >$1,500.00
    // -------------
    // [RSS+signature] will always be required and included in carrier-calc price, no user input needed
    return FormVariant.ALWAYS_COVERED;
  }
}

enum CoupledProtectionRadioOption {
  COUPLED_PROTECTION_YES = 'coupledProtectionYes',
  SIGNATURE_ONLY = 'signatureOnly',
  DECLINE_PROTECTION = 'declineProtection',
}

function currentRadioValue(packageEntry) {
  if (!packageEntry.forceSafeShipping && packageEntry.forceSignatureRequired) {
    return CoupledProtectionRadioOption.SIGNATURE_ONLY;
  } else if (packageEntry.forceSafeShipping) {
    return CoupledProtectionRadioOption.COUPLED_PROTECTION_YES;
  } else {
    return CoupledProtectionRadioOption.DECLINE_PROTECTION;
  }
}

export default function ShippingAddOnsCard() {
  const safeShippingHcUrl = useHelpCenterArticleUrl(SAFE_SHIPPING_ARTICLE_ID);
  const { listingEntry, shopConfig, dispatch } = useContext(SellFormContext);
  const packageEntry = listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const isInUsd = shopConfig.currencySetting === 'USD';
  const currentPriceInput = listingEntry[Field.PRICE]?.amount ?? '';
  const currentPriceCents = parseAmount(currentPriceInput, shopConfig.currencySetting).amountCents;
  const shouldShowForm = packageEntryComplete(packageEntry) &&
                          isInUsd &&
                          !!currentPriceInput &&
                          currentPriceCents > 0 &&
                          currentPriceCents <= RSS_MAX_USD_CENTS;

  if (!shouldShowForm) { return null; }

  const isUps = !!packageEntry.allowedCarriers?.length && packageEntry.allowedCarriers[0] === 'UPS';
  const formVariant = getFormVariant(currentPriceCents, isUps);
  const sectionTitleKey = formVariant === FormVariant.ALWAYS_COVERED ? 'alreadyProtectedHeader' : 'header';
  const sectionSubtitleKey = formVariant === FormVariant.ALWAYS_COVERED ? 'alreadyProtectedSubheader' : 'subheader';

  return (
    <FormCards.Card>
      <FormCards.FormSection
        title={I18n.t(`discovery.sellForm.shippingSection.shippingAddOnsCard.${sectionTitleKey}`)}
        subtitle={(
          <div className="mb-0">
            <span className="mr-1">
              {I18n.t(`discovery.sellForm.shippingSection.shippingAddOnsCard.${sectionSubtitleKey}`)}
            </span>
            <CoreLink
              to={safeShippingHcUrl}
              target="_blank"
            >
              {I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.learnMoreLinkText')}
            </CoreLink>
          </div>
        )}
        fullWidthForm
        icon={(
          <div className="mr-2" style={{ color: 'var(--rc-color-accent-loud)' }}>
            <RCIcon size="rc-space-12" svgComponent={ProtectionShieldIcon} />
          </div>
        )}
      >
        <div className="mb-4">
          {formVariant === FormVariant.RSS_OPTIONAL_ONLY && (
            <RCCheckbox
              checked={packageEntry.forceSafeShipping}
              label={I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.rssOptionLabel')}
              onChange={() => {
                dispatch({
                  type: ActionType.UPDATE_FIELD,
                  fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
                  value: { ...packageEntry, ...{ forceSafeShipping: !packageEntry.forceSafeShipping } },
                });
              }}
            />
          )}

          {formVariant === FormVariant.SIGNATURE_AND_RSS_BOTH_OPTIONAL && (
            <>
              <div className="mb-3">
                <RCCheckbox
                  checked={packageEntry.forceSafeShipping}
                  label={I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.rssOptionLabel')}
                  onChange={() => {
                    dispatch({
                      type: ActionType.UPDATE_FIELD,
                      fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
                      value: { ...packageEntry, ...{ forceSafeShipping: !packageEntry.forceSafeShipping } },
                    });
                  }}
                />
              </div>
              <RCCheckbox
                checked={packageEntry.forceSignatureRequired}
                label={I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.signatureCheckboxLabel')}
                sublabel={I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.signatureSublabel')}
                onChange={() => {
                  dispatch({
                    type: ActionType.UPDATE_FIELD,
                    fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
                    value: { ...packageEntry, ...{ forceSignatureRequired: !packageEntry.forceSignatureRequired } },
                  });
                }}
              />
            </>
          )}

          {formVariant === FormVariant.COUPLED_PROTECTION && (
            <div className="mb-4">
              <RCRadioGroup
                id="rss-coupled-with-signature"
                name="rss-coupled-with-signature"
                onChange={(event) => {
                  let updatedPackageEntry;

                  if (event.target.value === CoupledProtectionRadioOption.COUPLED_PROTECTION_YES) {
                    updatedPackageEntry = { ...packageEntry, ...{ forceSafeShipping: true, forceSignatureRequired: true } };
                  } else if (event.target.value === CoupledProtectionRadioOption.SIGNATURE_ONLY) {
                    updatedPackageEntry = { ...packageEntry, ...{ forceSafeShipping: false, forceSignatureRequired: true } };
                  } else if (event.target.value === CoupledProtectionRadioOption.DECLINE_PROTECTION) {
                    updatedPackageEntry = { ...packageEntry, ...{ forceSafeShipping: false, forceSignatureRequired: false } };
                  }

                  dispatch({
                    type: ActionType.UPDATE_FIELD,
                    fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
                    value: updatedPackageEntry,
                  });
                }}
                value={currentRadioValue(packageEntry)}
                label={I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.coupledRadioHeader')}
              >
                <RCRadioGroup.Option
                  value={CoupledProtectionRadioOption.COUPLED_PROTECTION_YES}
                  label={I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.rssOptionLabel')}
                  sublabel={I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.rssAndSignatureCoupledRadioSubLabel')}
                />
                <RCRadioGroup.Option
                  value={CoupledProtectionRadioOption.SIGNATURE_ONLY}
                  label={I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.signatureRequiredOnlyRadioLabel')}
                  sublabel={I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.signatureSublabel')}
                />
                <RCRadioGroup.Option
                  value={CoupledProtectionRadioOption.DECLINE_PROTECTION}
                  label={I18n.t('discovery.sellForm.shippingSection.shippingAddOnsCard.declineProtectionRadioLabel')}
                />
              </RCRadioGroup>
            </div>
          )}
        </div>
      </FormCards.FormSection>
    </FormCards.Card>
  );
}
